/**
 * Composite Analyzer: Setup Rule Validation (setup scope)
 *
 * The trader has documented intent in two structured places per setup:
 *   - setupDetails[name].rules[]                  — free-text "what defines this setup"
 *   - setupDetails[name].recommendedConfluences[] — pinned must-haves
 *
 * Right now NOTHING reads either of these (setupConfluenceFit only uses
 * recommendedConfluences to dedupe its own recommendations). The trader
 * documents an edge and Coach is blind to whether they're following it.
 *
 * This analyzer asks the basic compliance questions:
 *
 *   1. Does the setup have ANY documented rules / recommendedConfluences?
 *      If not, nudge — a documented edge is a measurable edge.
 *
 *   2. When recommendedConfluences exist: what % of trades on this setup
 *      had ALL of them present? "Compliance rate." Compare WR of compliant
 *      vs non-compliant trades — quantifies the cost of breaking the
 *      trader's own rules.
 *
 *   3. Among non-compliant trades, which recommendedConfluence is most
 *      often missing? Names the specific rule being skipped.
 *
 * Free-text `rules[]` strings are NOT NLP-matched (out of scope without an
 * LLM). They're only counted for the "do you have rules at all?" nudge.
 *
 * Reads:
 *   trade.confluences                                  — array of confluence IDs
 *   trade.netPnl                                       — for win/loss bucketing
 *   ctx.setupDetails[setupName].recommendedConfluences — pinned must-haves
 *   ctx.setupDetails[setupName].rules                  — for the nudge
 *   ctx.allConfluences                                 — for label resolution
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';

export const key = 'setupRuleValidation';
export const tier = 'free';

// Sample-size guards.
const COMPLIANCE_MIN_TRADES         = 6;     // Enough setup history to measure compliance
const COMPLIANCE_MIN_PER_GROUP      = 3;     // Need 3+ in EACH (compliant, non-compliant)
const COMPLIANCE_MIN_WR_GAP         = 0.15;  // 15pp WR gap between groups
const NUDGE_MIN_TRADES              = 8;     // Don't nudge until enough trades exist on this setup

const fmtPct = (n) => `${Math.round(n * 100)}%`;

const buildLabelMap = (allConfluences) => {
  const m = new Map();
  if (!Array.isArray(allConfluences)) return m;
  for (const c of allConfluences) {
    if (c?.id) m.set(c.id, c.label || c.name || c.id);
  }
  return m;
};
const labelFor = (id, labelMap) => labelMap.get(id) || id;

const isWin = (t) => Number.isFinite(t?.netPnl) && t.netPnl > 0;
const confluencesOf = (t) => (Array.isArray(t?.confluences) ? t.confluences.filter(Boolean) : []);

/**
 * A trade is "compliant" if its confluences[] is a SUPERSET of the setup's
 * recommendedConfluences[]. Extra confluences are fine — that's the trader
 * adding more conviction. Missing recommendations is the rule break.
 */
const isCompliant = (trade, recommended) => {
  if (!Array.isArray(recommended) || recommended.length === 0) return null;
  const have = new Set(confluencesOf(trade));
  for (const r of recommended) if (!have.has(r)) return false;
  return true;
};

// ── Pattern 1: Documentation nudge ───────────────────────────────────────
//
// If a setup has been used enough to have a real history but NEITHER a rule
// list NOR pinned confluences, surface a nudge. Documented edge = measurable
// edge — without anything written down, the rest of the analyzers can't
// validate.

const detectDocumentationGap = (setupName, setupTrades, setupDetails) => {
  if (setupTrades.length < NUDGE_MIN_TRADES) return null;
  const details = setupDetails?.[setupName] || {};
  const rules = Array.isArray(details.rules) ? details.rules : [];
  const recommended = Array.isArray(details.recommendedConfluences) ? details.recommendedConfluences : [];
  if (rules.length > 0 || recommended.length > 0) return null;

  const wins = setupTrades.filter(isWin).length;
  const wr = setupTrades.length > 0 ? wins / setupTrades.length : 0;

  return makeObservation({
    id: `${key}:${setupName}:doc-gap`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.INFO,
    category: CATEGORY.PROCESS,
    title: `"${setupName}" has ${setupTrades.length} trades but no documented rules or confluences`,
    detail: `Current WR ${fmtPct(wr)}. Without written rules or pinned confluences, Coach can't validate whether you're following your own edge — every analyzer that compares "what you said you'd do" against "what you actually did" is blocked here.`,
    action: `Open the setup card and add 2-3 rules + pin the confluences this setup requires. Even rough drafts unlock the rest of the validation analyzers.`,
    evidence: { tradeCount: setupTrades.length, wr, hasRules: false, hasConfluencePins: false },
    confidence: 0.85,
    priority: 60,
  });
};

// ── Pattern 2: Compliance WR gap ─────────────────────────────────────────
//
// When recommendedConfluences exist, split trades into compliant (all
// recommendations present) vs non-compliant (at least one missing). Compare
// WR. If the gap is meaningful AND in the expected direction (compliant
// wins more), the trader's rules are real and being broken matters.

const detectComplianceGap = (setupName, setupTrades, setupDetails) => {
  if (setupTrades.length < COMPLIANCE_MIN_TRADES) return null;
  const recommended = setupDetails?.[setupName]?.recommendedConfluences || [];
  if (!Array.isArray(recommended) || recommended.length === 0) return null;

  const compliant = [];
  const broken = [];
  for (const t of setupTrades) {
    const c = isCompliant(t, recommended);
    if (c === true) compliant.push(t);
    else if (c === false) broken.push(t);
  }
  if (compliant.length < COMPLIANCE_MIN_PER_GROUP) return null;
  if (broken.length    < COMPLIANCE_MIN_PER_GROUP) return null;

  const wr = (group) => group.filter(isWin).length / group.length;
  const cWr = wr(compliant);
  const bWr = wr(broken);
  const gap = cWr - bWr;
  // Only fire when compliance ACTUALLY helps. If breaking the rules wins
  // more, that's a different (interesting) signal — handled below.
  if (Math.abs(gap) < COMPLIANCE_MIN_WR_GAP) return null;

  if (gap > 0) {
    // Rules-help case: encourage adherence.
    const complianceRate = compliant.length / setupTrades.length;
    return makeObservation({
      id: `${key}:${setupName}:compliance-gap-positive`,
      analyzer: key,
      scope: 'setup',
      scopeId: setupName,
      severity: SEVERITY.WARNING,
      category: CATEGORY.DISCIPLINE,
      title: `"${setupName}" rule compliance pays ${fmtPct(gap)} of WR — but you only follow them ${fmtPct(complianceRate)} of the time`,
      detail: `Trades with ALL recommended confluences present: ${fmtPct(cWr)} WR (${compliant.length}/${setupTrades.length}). Trades missing at least one: ${fmtPct(bWr)} WR (${broken.length}/${setupTrades.length}). The recommendation list is doing its job — when you actually use it.`,
      action: `Treat the recommended confluences as required, not optional. The compliance gap IS the edge on this setup.`,
      evidence: {
        compliantCount: compliant.length,
        brokenCount: broken.length,
        compliantWr: cWr,
        brokenWr: bWr,
        gap,
        complianceRate,
        recommendedCount: recommended.length,
      },
      confidence: 0.82,
      priority: 84,
    });
  }
  // Rules-don't-help case: the recommendations might be wrong, or the setup
  // has evolved past them. Surface so the trader revisits the pinned list.
  return makeObservation({
    id: `${key}:${setupName}:compliance-gap-inverse`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.INFO,
    category: CATEGORY.PROCESS,
    title: `"${setupName}" pinned confluences may be stale — non-compliant trades win ${fmtPct(-gap)} more`,
    detail: `Trades following all recommended confluences: ${fmtPct(cWr)} WR (${compliant.length}). Trades missing at least one: ${fmtPct(bWr)} WR (${broken.length}). The recommendation list isn't paying — either the wrong confluences are pinned, or the setup has evolved.`,
    action: `Revisit the recommended-confluences list. Compare it against what the actual winners share (see the "winning recipe" insight from confluenceComposite).`,
    evidence: {
      compliantCount: compliant.length,
      brokenCount: broken.length,
      compliantWr: cWr,
      brokenWr: bWr,
      gap,
      recommendedCount: recommended.length,
    },
    confidence: 0.7,
    priority: 68,
  });
};

// ── Pattern 3: Most-skipped recommended confluence ───────────────────────
//
// Among non-compliant trades, which recommendation is most often missing?
// Names the specific rule that's being broken so the trader can address it
// directly instead of getting a generic "follow your rules" nag.

const detectMostSkipped = (setupName, setupTrades, setupDetails, labelMap) => {
  const recommended = setupDetails?.[setupName]?.recommendedConfluences || [];
  if (!Array.isArray(recommended) || recommended.length === 0) return null;
  if (setupTrades.length < COMPLIANCE_MIN_TRADES) return null;

  const broken = setupTrades.filter(t => isCompliant(t, recommended) === false);
  if (broken.length < COMPLIANCE_MIN_PER_GROUP) return null;

  const missingFreq = new Map();
  for (const t of broken) {
    const have = new Set(confluencesOf(t));
    for (const r of recommended) {
      if (!have.has(r)) missingFreq.set(r, (missingFreq.get(r) || 0) + 1);
    }
  }
  if (missingFreq.size === 0) return null;

  const sorted = [...missingFreq.entries()].sort((a, b) => b[1] - a[1]);
  const [topId, topCount] = sorted[0];
  const skipShare = topCount / broken.length;
  // Only surface if one confluence dominates the skip pattern (≥50% of breaks).
  if (skipShare < 0.5) return null;

  const topLabel = labelFor(topId, labelMap);

  // WR on trades missing JUST this confluence (might have all others).
  const missingTopOnly = setupTrades.filter(t => !confluencesOf(t).includes(topId));
  const haveTop = setupTrades.filter(t => confluencesOf(t).includes(topId));
  const missingWr = missingTopOnly.length > 0
    ? missingTopOnly.filter(isWin).length / missingTopOnly.length : null;
  const haveWr = haveTop.length > 0
    ? haveTop.filter(isWin).length / haveTop.length : null;

  const wrPart = (missingWr != null && haveWr != null)
    ? ` Trades WITH ${topLabel}: ${fmtPct(haveWr)} WR (${haveTop.length}). Trades WITHOUT: ${fmtPct(missingWr)} WR (${missingTopOnly.length}).`
    : '';

  return makeObservation({
    id: `${key}:${setupName}:most-skipped:${topId}`,
    analyzer: key,
    scope: 'setup',
    scopeId: setupName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.DISCIPLINE,
    title: `"${setupName}" — ${topLabel} is the rule you skip (${topCount}/${broken.length} non-compliant trades).`,
    detail: `Across ${broken.length} trades that broke at least one recommended confluence on this setup, ${topLabel} was missing ${fmtPct(skipShare)} of the time.${wrPart}`,
    action: `Add a hard pre-trade check for ${topLabel} on this setup. It's the recommendation you've documented but stopped using.`,
    evidence: {
      mostSkippedId: topId,
      mostSkippedLabel: topLabel,
      missingCount: topCount,
      brokenSampleSize: broken.length,
      skipShare,
      missingWr,
      haveWr,
    },
    confidence: 0.78,
    priority: 78,
  });
};

// ── Public analyze() ─────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { setupName, setupTrades, setupDetails = {}, allConfluences = [] } = ctx || {};
  if (!setupName || !Array.isArray(setupTrades) || setupTrades.length === 0) return null;

  // Live-trade gate: outcome-based analysis must run on closed trades only.
  // Unrealized P&L on open positions isn't a realized outcome — including it
  // in WR / compliance / loss-cause classification fires premature "X% won"
  // claims while the user is still in the trade (panic-exit risk). Matches
  // summarizePnl semantics.
  const closedSetupTrades = setupTrades.filter(t => t?.isOpen !== true);
  if (closedSetupTrades.length === 0) return null;

  const labelMap = buildLabelMap(allConfluences);
  const out = [];

  // Documentation nudge fires only when there's NOTHING — so it's mutually
  // exclusive with compliance/skip patterns (those need rules to exist).
  const docGap = detectDocumentationGap(setupName, closedSetupTrades, setupDetails);
  if (docGap) out.push(docGap);

  const compliance = detectComplianceGap(setupName, closedSetupTrades, setupDetails);
  if (compliance) out.push(compliance);

  const skipped = detectMostSkipped(setupName, closedSetupTrades, setupDetails, labelMap);
  if (skipped) out.push(skipped);

  return out.length === 0 ? null : out;
};

// Exports for tests
export {
  buildLabelMap,
  isCompliant,
  detectDocumentationGap,
  detectComplianceGap,
  detectMostSkipped,
  COMPLIANCE_MIN_TRADES,
  COMPLIANCE_MIN_PER_GROUP,
  COMPLIANCE_MIN_WR_GAP,
  NUDGE_MIN_TRADES,
};
